import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const SellerKioskOrderView = () => {
  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [data, setData] = useState(null);

  const params = new URLSearchParams(window.location.search);
  const orderId = parseInt(params.get('order_id') || '0', 10);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(
          `/api/seller/kiosk/order/view-data?order_id=${encodeURIComponent(orderId)}`,
          { withCredentials: true }
        );

        if (res.data && res.data.ok) {
          setData(res.data);
          return;
        }

        alert(res.data?.msg || '주문 정보를 불러오지 못했습니다.');
      } catch (err) {
        const redirect = err?.response?.data?.redirect;
        if (redirect) {
          window.location.href = redirect;
          return;
        }
        alert('주문 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [orderId]);

  const order = data?.order || null;
  const items = Array.isArray(data?.items) ? data.items : [];
  const delivery = data?.delivery || null;
  const logs = Array.isArray(data?.logs) ? data.logs : [];

  {/* 가격계산 */}
  const optionTotalPrice = items.reduce((sum, item) => {
    return (
      sum +
      Number(item.option_total || 0) *
        Number(item.quantity || 0)
    );
  }, 0);
  
  const productOriginalPrice =
    Number(order?.total_price || 0) +
    Number(order?.product_discount_amount || 0) -
    optionTotalPrice;
  
  const productDiscountAmount =
    Number(order?.product_discount_amount || 0);
  
  const couponDiscountAmount =
    Number(order?.discount_amount || 0);
  
  const deliveryCost =
    Number(order?.delivery_cost || 0);
  
  const finalPaymentPrice =
    Number(order?.result_price || order?.total_price || 0);

  const view = useMemo(() => {
    if (!order) {
      return {
        showTotal: 0,
        channel: 'web',
        channelLabel: '웹 주문',
        isDelivery: false,
        isPickup: false,
        address: '',
        deliveryStatusNow: '',
        currentStatusLabel: '',
      };
    }

    const showTotal =
      Number(order.result_price || 0) > 0
        ? Number(order.result_price || 0)
        : Number(order.total_price || 0);

    const channel = (order.channel || 'web').trim();
    const channelLabel = channel === 'kiosk' ? '키오스크 주문' : '웹 주문';

    const addrRaw = (delivery?.address || '').trim();
    const deliveryStatusNow = delivery?.delivery_status || '';
    const pickupKeywords = ['매장 픽업', '매장픽업', '픽업'];

    let isDelivery = false;
    let isPickup = false;
    let address = '';

    if (addrRaw !== '') {
      address = addrRaw;

      const hasPickupWord = pickupKeywords.some((kw) => addrRaw.includes(kw));
      if (hasPickupWord) {
        isPickup = true;
        isDelivery = false;
      } else {
        isDelivery = true;
        isPickup = false;
      }
    } else {
      isPickup = true;
      isDelivery = false;
    }

    let currentStatusLabel = order.current_status || 'NEW';

    if (isDelivery) {
      currentStatusLabel =
        deliveryStatusNow === '' ? '결제완료' : deliveryStatusNow;
    } else {
      const map = {
        NEW: '새주문',
        ACCEPTED: '접수',
        COOKING: '준비중',
        DONE: '완료',
        CANCELLED: '취소',
      };
      currentStatusLabel = map[currentStatusLabel] || currentStatusLabel;
    }

    return {
      showTotal,
      channel,
      channelLabel,
      isDelivery,
      isPickup,
      address,
      deliveryStatusNow,
      currentStatusLabel,
    };
  }, [order, delivery]);

  const goBackList = () => {
    window.history.back();
  };

  const updateDeliveryStatus = async (deliveryStatus) => {
    if (!window.confirm(`배송상태를 ${deliveryStatus}(으)로 변경할까요?`)) return;

    try {
      const res = await axios.post(
        '/api/seller/delivery/status-json',
        {
          order_id: orderId,
          delivery_status: deliveryStatus,
        },
        {
          withCredentials: true,
        }
      );

      if (!res.data?.ok) {
        alert(res.data?.msg || res.data?.error || '배송상태 변경 실패');
        return;
      }

      window.location.reload();
    } catch (err) {
      alert(
        err?.response?.data?.msg ||
          err?.response?.data?.error ||
          '배송상태 변경 중 오류가 발생했습니다.'
      );
    }
  };

  const updateOrderStatus = async (currentStatus) => {
    if (!window.confirm(`주문상태를 ${currentStatus}(으)로 변경할까요?`)) return;

    try {
      const res = await axios.post(
        '/api/seller/kiosk/order/update',
        new URLSearchParams({
          order_id: orderId,
          current_status: currentStatus,
        }),
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (!res.data?.ok) {
        alert(res.data?.msg || '주문상태 변경 실패');
        return;
      }

      window.location.reload();
    } catch (err) {
      alert(err?.response?.data?.msg || '주문상태 변경 중 오류가 발생했습니다.');
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
  
    const cleaned = String(value)
      .replace('GMT', '')
      .replace(',', '')
      .trim();
  
    const date = new Date(cleaned);
  
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.page}>
          <div style={styles.loadingBox}>로딩중...</div>
        </div>
      </SellerLayout>
    );
  }

  if (!order) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.page}>
          <div style={styles.emptyBox}>주문 정보를 찾을 수 없습니다.</div>
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <div style={styles.page}>
        <div style={styles.inner}>
          <div style={styles.topBar}>
            <span type="button" onClick={goBackList} 
              style={{
                fontSize: '15px',
                fontWeight: '500',
                cursor: 'pointer',
                color: '#212529',
                userSelect: 'none',
              }}
            >
              ← 목록
            </span>

            <div style={styles.topTitle}>주문 상세 #{Number(orderId || 0)}</div>

            <div style={{ width: 56 }} />
          </div>

          <div style={styles.card}>
            <div style={styles.cardBody}>
              <div style={styles.summaryRow}>
                <div>
                  <div style={styles.mutedSmall}>주문시간</div>
                  <div style={styles.strongText}>
                    {formatDate(order.order_date)}
                  </div>
                </div>

                <div style={styles.rightBox}>
                  <div style={styles.mutedSmall}>총 결제금액</div>
                  <div style={styles.totalPrice}>
                    {Number(view.showTotal || 0).toLocaleString('ko-KR')}원
                  </div>
                </div>
              </div>

              <hr style={styles.hr} />

              <div style={styles.infoGrid}>
                <div style={styles.col6}>
                  <div style={styles.mutedSmall}>주문자</div>
                  <div style={styles.strongText}>{order.buyer_name || ''}</div>
                </div>

                <div style={styles.col6}>
                  <div style={styles.mutedSmall}>전화</div>
                  <div style={styles.strongText}>{order.buyer_phone || ''}</div>
                </div>

                <div style={styles.col12}>
                  <div style={styles.mutedSmall}>현재 상태</div>
                  <div style={styles.currentStatus}>
                    {view.currentStatusLabel || ''}
                  </div>
                </div>

                <div style={styles.col12}>
                  <div style={styles.mutedSmall}>주문 유형</div>
                  <div style={styles.badgeWrap}>
                    <span
                      style={{
                        ...styles.badge,
                        ...(view.channel === 'kiosk'
                          ? styles.badgeDark
                          : styles.badgeSecondary),
                      }}
                    >
                      {view.channelLabel}
                    </span>
                  </div>
                </div>

                {view.isDelivery && (
                  <div style={styles.col12}>
                    <div style={styles.mutedSmall}>배달 주소</div>
                    <div style={styles.strongTextPre}>{view.address}</div>
                  </div>
                )}

                {/* 현재 API에는 pickup_datetime/pickup_status가 없음
                    PHP처럼 완전 동일하게 하려면 backend 응답에 pickup도 포함돼야 함 */}
              </div>

              <div style={styles.buttonRow}>
                {view.isDelivery ? (
                  <>
                    <button
                      type="button"
                      style={{ ...styles.actionBtn, ...styles.btnWarning }}
                      onClick={() => updateDeliveryStatus('배송준비중')}
                    >
                      배송준비중
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.actionBtn, ...styles.btnInfo }}
                      onClick={() => updateDeliveryStatus('배송중')}
                    >
                      배송중
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.actionBtn, ...styles.btnSuccess }}
                      onClick={() => updateDeliveryStatus('배송완료')}
                    >
                      배송완료
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      style={{ ...styles.actionBtn, ...styles.btnWarning }}
                      onClick={() => updateOrderStatus('COOKING')}
                    >
                      준비중
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.actionBtn, ...styles.btnSuccess }}
                      onClick={() => updateOrderStatus('DONE')}
                    >
                      완료
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.actionBtn, ...styles.btnSecondary }}
                      onClick={() => updateOrderStatus('CANCELLED')}
                    >
                      취소
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardBody}>
              <div style={styles.sectionTitle}>주문 상품</div>

              {items.length === 0 ? (
                <div style={styles.emptyList}>주문 상품이 없습니다.</div>
              ) : (
                items.map((it, idx) => {
                  const name =
                    it.product_name && String(it.product_name).trim() !== ''
                      ? it.product_name
                      : `상품#${Number(it.product_id || 0)}`;
                
                  const qty = Number(it.quantity || 0);
                
                  // 주문 당시 저장된 상품가격
                  const orderedProductPrice = Number(it.price || 0);
                
                  // 주문 당시 저장된 옵션가격
                  const optionTotal = Number(it.option_total || 0);
                
                  // OrderDetail과 동일한 계산
                  const itemTotalPrice =
                    (orderedProductPrice + optionTotal) * qty;
                
                  const hasOption =
                    Array.isArray(it.options) && it.options.length > 0;
                
                  const imagePath =
                  it.image_path && String(it.image_path).trim() !== ''
                    ? String(it.image_path).startsWith('http')
                      ? String(it.image_path)
                      : `/static/${String(it.image_path).replace(/^\/+/, '')}`
                    : '/static/default_thumb.jpg';
                
                  return (
                    <div
                      key={`${it.product_id || idx}-${idx}`}
                      style={styles.itemRow}
                    >
                      <div style={styles.itemLeft}>
                        {imagePath ? (
                          <img
                            src={imagePath}
                            alt={name}
                            style={styles.thumb}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div style={styles.thumbPlaceholder} />
                        )}
                
                        <div style={styles.itemContent}>
                          <div style={styles.itemName}>
                            {name}
                          </div>
                
                          {hasOption && (
                            <div style={styles.optionBox}>
                              {it.options.map((opt, optIdx) => (
                                <div
                                  key={`${opt.option_group_id || optIdx}-${opt.option_item_id || optIdx}`}
                                  style={styles.optionText}
                                >
                                  {opt.option_group_name}: {opt.option_item_name}
                
                                  {Number(opt.option_price || 0) > 0 &&
                                    ` (+${Number(
                                      opt.option_price || 0
                                    ).toLocaleString('ko-KR')}원)`}
                                </div>
                              ))}
                            </div>
                          )}
                
                          <div style={styles.itemMeta}>
                            수량: {qty}
                          </div>
                        </div>
                      </div>
                
                      <div style={styles.itemPrice}>
                        {itemTotalPrice.toLocaleString('ko-KR')}원
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardBody}>
              <div style={styles.sectionTitle}>결제금액</div>

              <div style={styles.priceRow}>
                <span>상품금액</span>
                <span>
                  {productOriginalPrice.toLocaleString('ko-KR')}원
                </span>
              </div>

              {optionTotalPrice > 0 && (
                <div style={styles.priceRow}>
                  <span>옵션금액</span>
                  <span>
                    +{optionTotalPrice.toLocaleString('ko-KR')}원
                  </span>
                </div>
              )}

              <div style={styles.priceRow}>
                <span>상품할인</span>
                <span>
                  -{productDiscountAmount.toLocaleString('ko-KR')}원
                </span>
              </div>

              <div style={styles.priceRow}>
                <span>쿠폰할인</span>
                <span>
                  -{couponDiscountAmount.toLocaleString('ko-KR')}원
                </span>
              </div>

              <div style={styles.priceRow}>
                <span>배송료</span>
                <span>
                  +{deliveryCost.toLocaleString('ko-KR')}원
                </span>
              </div>

              <div style={styles.priceDivider} />

              <div style={styles.finalPriceRow}>
                <span>최종 결제금액</span>
                <span>
                  {finalPaymentPrice.toLocaleString('ko-KR')}원
                </span>
              </div>
            </div>
          </div>

          {logs.length > 0 && (
            <div style={styles.card}>
              <div style={styles.cardBody}>
                <div style={styles.sectionTitle}>상태 변경 로그</div>

                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>이전상태</th>
                        <th style={styles.th}>변경상태</th>
                        <th style={styles.th}>변경시간</th>
                        <th style={styles.th}>변경자</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, idx) => (
                        <tr key={idx}>
                          <td style={styles.td}>{log.old_status || ''}</td>
                          <td style={styles.td}>{log.new_status || ''}</td>
                          <td style={styles.td}>
                            {formatDate(log.changed_at)}
                          </td>
                          <td style={styles.td}>{log.changed_by || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SellerLayout>
  );
};

const styles = {
  page: {
    background: '#f7f7f7',
    minHeight: '100%',
    fontFamily: "'Noto Sans KR', sans-serif",

    margin: '-16px',
    padding: '16px',
  },
  inner: {
    maxWidth: '1000px',
    margin: '0 auto',
  },
  loadingBox: {
    padding: '40px 20px',
    textAlign: 'center',
    background: '#fff',
    border: 0,
    borderRadius: '14px',
    boxShadow: '0 6px 18px rgba(0,0,0,.06)',
    color: '#6c757d',
  },
  emptyBox: {
    padding: '40px 20px',
    textAlign: 'center',
    background: '#fff',
    border: 0,
    borderRadius: '14px',
    boxShadow: '0 6px 18px rgba(0,0,0,.06)',
    color: '#6c757d',
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    gap: '12px',
  },
  listButton: {
    border: '1px solid #6c757d',
    background: '#fff',
    color: '#495057',
    fontSize: '13px',
    padding: '6px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  topTitle: {
    fontWeight: 700,
    color: '#212529',
    fontSize: '16px',
  },
  card: {
    border: 0,
    borderRadius: '14px',
    boxShadow: '0 6px 18px rgba(0,0,0,.06)',
    background: '#fff',
    marginBottom: '16px',
  },
  cardBody: {
    padding: '20px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  rightBox: {
    textAlign: 'right',
  },
  mutedSmall: {
    color: '#6c757d',
    fontSize: '12px',
    marginBottom: '4px',
  },
  strongText: {
    fontWeight: 600,
    color: '#212529',
    fontSize: '15px',
  },
  strongTextPre: {
    fontWeight: 600,
    color: '#212529',
    fontSize: '15px',
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
  },
  totalPrice: {
    fontWeight: 700,
    fontSize: '15px',
    color: '#212529',
  },
  hr: {
    height: '1px',
    background: '#000',
    border: 'none',
    margin: '14px 0',
  },
  infoGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: '10px',
  },
  col6: {
    width: '50%',
    paddingRight: '8px',
    boxSizing: 'border-box',
  },
  col12: {
    width: '100%',
    marginTop: '8px',
  },
  currentStatus: {
    fontWeight: 700,
    color: '#212529',
    fontSize: '18px',
  },
  badgeWrap: {
    marginTop: '2px',
  },
  badge: {
    display: 'inline-block',
    padding: '6px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1,
  },
  badgeDark: {
    background: '#212529',
    color: '#fff',
  },
  badgeSecondary: {
    background: '#6c757d',
    color: '#fff',
  },
  buttonRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
    flexWrap: 'wrap',
  },
  actionBtn: {
    border: 0,
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnWarning: {
    background: '#ffc107',
    color: '#212529',
  },
  btnInfo: {
    background: '#0dcaf0',
    color: '#fff',
  },
  btnSuccess: {
    background: '#198754',
    color: '#fff',
  },
  btnSecondary: {
    background: '#6c757d',
    color: '#fff',
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: '10px',
    color: '#212529',
  },
  emptyList: {
    color: '#6c757d',
    padding: '12px 0',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #e9ecef',
    gap: '12px',
  },
  itemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
    flex: 1,
    fontSize: '15px',
  },
  thumb: {
    width: '56px',
    height: '56px',
    objectFit: 'cover',
    borderRadius: '10px',
    background: '#eee',
    flexShrink: 0,
  },
  thumbPlaceholder: {
    width: '56px',
    height: '56px',
    borderRadius: '10px',
    background: '#eee',
    flexShrink: 0,
  },
  itemName: {
    fontWeight: 600,
    color: '#212529',
    marginBottom: '4px',
    wordBreak: 'break-word',
  },
  itemMeta: {
    fontSize: '13px',
    color: '#6c757d',
  },
  itemPrice: {
    fontWeight: 700,
    color: '#212529',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    fontSize: '14px',
    alignSelf: 'flex-start',
  },
  tableWrap: {
    width: '100%',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'center',
    background: '#f8f9fa',
    padding: '10px',
    borderBottom: '1px solid #dee2e6',
    color: '#6c757d',
    whiteSpace: 'nowrap',
  },
  td: {
    textAlign: 'center',
    padding: '12px 10px',
    borderBottom: '1px solid #f1f3f5',
    color: '#212529',
    whiteSpace: 'nowrap',
  },
  optionBox: {
    marginTop: '4px',
    marginBottom: '4px',
  },
  
  optionText: {
    fontSize: '13px',
    color: '#6c757d',
    lineHeight: 1.5,
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#555',
  },
  
  priceDivider: {
    height: '1px',
    background: '#eee',
    margin: '12px 0',
  },
  
  finalPriceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 700,
    fontSize: '16px',
    color: '#212529',
  },
};

export default SellerKioskOrderView;